/**
 * Every kind of prop: its geometry, material and how many can stand on the largest planet. The
 * look picks the materials: today's softly lit ones with shadows, or the stylized ones that sway in
 * the wind and cast none. Grass tufts (the stylized look) are a kind of their own, drawn near the
 * player by grass.ts.
 */
import * as THREE from 'three';

import { ART_KINDS } from '../assets/manifest';
import { standIn } from '../assets/standIns';
import { hubPad, hubRing } from '../models/hub';
import type { MachinePart } from '../models/part';
import { ALL_MACHINE_PARTS } from '../models/machines';
import { tuftGeometry } from '../models/tuft';
import { softMaterial } from '../stage/scene';
import type { Look } from '../style/look';
import { propMaterial } from '../style/propMaterial';
import { spheres } from './geometry';

export interface PropKind {
  geometry: THREE.BufferGeometry;
  material: THREE.Material;
  max: number;
  /** Each instance has its own colour. */
  colored: boolean;
  shadow: boolean;
}

/** How a prop is painted, in either look. */
interface Paint {
  color: number;
  /** The classic look's material settings. */
  classic?: THREE.MeshStandardMaterialParameters;
  /** How far the wind moves its tips in the stylized look; 0 or absent: still. */
  wind?: number;
  /** A glow of its own (crystals, an extractor's core). */
  glow?: { color: number; intensity: number };
  /** The glow pulses (the stylized look). */
  pulse?: boolean;
}

function material(look: Look, paint: Paint): THREE.Material {
  if (look === 'classic') return softMaterial(paint.color, paint.classic);
  return propMaterial({
    color: paint.color,
    wind: paint.wind ?? 0,
    ...(paint.glow && { emissive: paint.glow.color, emissiveIntensity: paint.glow.intensity }),
    ...(paint.pulse && { pulse: true }),
  });
}

/**
 * The small props there are many of, as the stylized look draws them: a faceted pebble and a
 * faceted flower of 20 triangles each instead of a smooth sphere of 80 and 40.
 */
const pebble = (look: Look): THREE.BufferGeometry =>
  look === 'stylized'
    ? new THREE.IcosahedronGeometry(0.11, 0).scale(1.35, 0.62, 1.05).translate(0, 0.035, 0)
    : new THREE.SphereGeometry(0.11, 8, 6).scale(1.35, 0.62, 1.05).translate(0, 0.035, 0);
/** A broken chunk of rock, angular: rubble a charge left (rules 24). */
const chunk = (): THREE.BufferGeometry =>
  new THREE.DodecahedronGeometry(0.075, 0).scale(1.25, 0.8, 1).translate(0, 0.03, 0);
const bloom = (look: Look): THREE.BufferGeometry =>
  look === 'stylized'
    ? new THREE.IcosahedronGeometry(0.045, 0).translate(0, 0.055, 0)
    : new THREE.SphereGeometry(0.045, 6, 4).translate(0, 0.055, 0);

function catalog(look: Look): Record<string, PropKind> {
  const shadows = look === 'classic';
  const prop = (
    geometry: THREE.BufferGeometry,
    paint: Paint,
    max: number,
    colored: boolean,
    shadow: boolean,
  ): PropKind => ({
    geometry,
    material: material(look, paint),
    max,
    colored,
    shadow: shadows && shadow,
  });
  const part = (p: MachinePart, max: number): PropKind =>
    prop(
      p.geometry,
      {
        color: p.color,
        classic: {
          roughness: p.roughness,
          metalness: p.metalness,
          ...(p.glow !== undefined && {
            emissive: p.glow,
            emissiveIntensity: p.glowStrength ?? 0.9,
          }),
        },
        ...(p.glow !== undefined && {
          glow: { color: p.glow, intensity: p.glowStrength ?? 0.9 },
        }),
        ...(p.pulse && { pulse: true }),
      },
      max,
      false,
      p.shadow,
    );

  const kinds: Record<string, PropKind> = {
    trunk: prop(
      new THREE.CylinderGeometry(0.045, 0.065, 0.36, 7).translate(0, 0.18, 0),
      { color: 0xa88468, wind: 0.05 },
      7000,
      false,
      true,
    ),
    crown: prop(
      new THREE.SphereGeometry(0.29, 12, 9).scale(1, 1.06, 1).translate(0, 0.56, 0),
      { color: 0xffffff, wind: 0.06 },
      5000,
      true,
      true,
    ),
    tall: prop(
      spheres([
        [0.22, 0.5],
        [0.16, 0.78],
      ]),
      { color: 0xffffff, wind: 0.06 },
      3000,
      true,
      true,
    ),
    bush: prop(
      new THREE.SphereGeometry(0.16, 10, 7).scale(1, 0.86, 1).translate(0, 0.1, 0),
      { color: 0x5a9444, wind: 0.25 },
      6000,
      false,
      true,
    ),
    stone: prop(pebble(look), { color: 0xcbd3de }, 4000, false, false),
    // stone a stonecutter draws from: warm, glowing and pulsing like the ground under it
    'stone.lit': prop(
      pebble(look),
      {
        color: 0xe8cfa6,
        classic: { roughness: 0.5, emissive: 0xf2952e, emissiveIntensity: 0.45 },
        glow: { color: 0xf2952e, intensity: 0.5 },
        pulse: true,
      },
      1200,
      false,
      false,
    ),
    // rock a charge broke: chunks in the rock's own colour
    rubble: prop(chunk(), { color: 0xffffff, classic: { roughness: 0.9 } }, 4000, true, false),
    flower: prop(
      bloom(look),
      { color: 0xffffff, classic: { roughness: 0.6 }, wind: 3 },
      9000,
      true,
      false,
    ),
    lily: prop(
      new THREE.CylinderGeometry(0.085, 0.085, 0.012, 10).translate(0, 0.006, 0),
      { color: 0x9ccb86 },
      1500,
      false,
      false,
    ),
    shard: prop(
      new THREE.OctahedronGeometry(0.09).scale(0.8, 1.5, 0.8).translate(0, 0.08, 0),
      {
        color: 0x9e7fe0,
        classic: { roughness: 0.45, emissive: 0x3c2a6e, emissiveIntensity: 0.5 },
        glow: { color: 0x3c2a6e, intensity: 0.5 },
      },
      6000,
      false,
      false,
    ),
    // the crystals of ore an extractor draws from: brighter, pulsing
    'shard.lit': prop(
      new THREE.OctahedronGeometry(0.09).scale(0.8, 1.5, 0.8).translate(0, 0.08, 0),
      {
        color: 0x9e7fe0,
        classic: { roughness: 0.4, emissive: 0x9d6bff, emissiveIntensity: 1.2 },
        glow: { color: 0x9d6bff, intensity: 1.2 },
        pulse: true,
      },
      1200,
      false,
      false,
    ),
  };
  if (look === 'stylized') {
    // the art (docs/ART.md): every kind starts as its family's stand-in, until the .glb loads
    for (const [name, art] of ART_KINDS) {
      kinds[name] = {
        geometry: standIn(art.family),
        material: propMaterial({ color: 0xffffff, wind: art.wind, vertexColors: true }),
        max: 3000,
        colored: true,
        shadow: false,
      };
    }
  }
  for (const p of ALL_MACHINE_PARTS) kinds[p.name] = part(p, 60);
  // the hub: one on every planet
  kinds['hub.pad'] = prop(
    hubPad(),
    { color: 0xe2ddd0, classic: { roughness: 0.85 } },
    4,
    false,
    true,
  );
  kinds['hub.ring'] = prop(
    hubRing(),
    {
      color: 0x5fe0c8,
      classic: { roughness: 0.4, emissive: 0x2fb8a0, emissiveIntensity: 0.9 },
      glow: { color: 0x2fb8a0, intensity: 0.9 },
    },
    4,
    false,
    false,
  );
  return kinds;
}

let tuft: PropKind | null = null;

/** The grass tuft of the stylized look, made once: thin blades, coloured per instance, in the wind. */
export function tuftKind(): PropKind {
  tuft ??= {
    geometry: tuftGeometry(),
    material: propMaterial({ color: 0xffffff, wind: 0.9, vertexColors: true, doubleSided: true }),
    max: 4000,
    colored: true,
    shadow: false,
  };
  return tuft;
}

const catalogs = new Map<Look, Readonly<Record<string, PropKind>>>();

/** The prop kinds of a look, made once. */
export function propKinds(look: Look): Readonly<Record<string, PropKind>> {
  let kinds = catalogs.get(look);
  if (!kinds) {
    kinds = catalog(look);
    catalogs.set(look, kinds);
  }
  return kinds;
}
